import React, { useState, useEffect } from 'react';
import DataCollection from '../../shared/DataCollection.js';
import TemperatureCardList from './TemperatureCardList.js';
import AreaDropdown from '../../shared/dropdowns/AreaDropdown.js';
import { useTranslation } from 'react-i18next';
import { Fetch } from '../../utils/Utils.js';
import Patch from '../../utils/Patch.js';
import { formatDate } from '../../../utils/dateFormat.js';
import '../../herd/ListHerd.css';

const dateRangeToRegex = (range) => {
  const now = new Date();
  let months;
  switch (range) {
    case 'this_month': months = 1;  break;
    case 'last_3_months': months = 3;  break;
    case 'last_year': months = 12; break;
    default: return null;
  }
  const parts = [];
  for (let i = 0; i < months; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    parts.push(`${yyyy}-${mm}`);
  }
  return `^(${parts.join('|')})`;
};

export const ListTemperatures = ({ actions }) => {
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [desktopFiltersOpen, setDesktopFiltersOpen] = useState(false);
  const [searchFields, setSearchFields] = useState({});
  const [dateRange, setDateRange] = useState('');
  const [areas, setAreas] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const collection = 'temperatures';

  useEffect(() => {
    Fetch("api/data/areas", "get", null,
      (data) => setAreas(data),
      () => console.log("error loading areas")
    );
  }, []);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const onSearchArea = (e) => {
    const value = e.target.value;
    setSearchFields(prev => {
      const next = {...prev};
      if (value) { next.area = value; } else { delete next.area; }
      return next;
    });
  };

  const onSearchDate = (e) => {
    const value = e.target.value;
    setDateRange(value);
    setSearchFields(prev => {
      const next = {...prev};
      const regex = dateRangeToRegex(value);
      if (regex) { next.date = regex; } else { delete next.date; }
      return next;
    });
  };

  const activeFilterCount = [searchFields.area, searchFields.date].filter(Boolean).length;

  const resetFilters = () => {
    setDateRange('');
    setSearchFields(prev => {
      const next = {...prev};
      delete next.area;
      delete next.date;
      return next;
    });
  };

  const columns = [
    {name: t("temperature"), selector: row => `${row.temperature} Celsius`, sortable:true},
    {name: t("date"),        selector: row => row.date?.substring(0,10) || '', sortable:true, cell: row => formatDate(row.date?.substring(0,10) || '')},
    {
      name: t("area"),
      sortable: true,
      selector: row => row.area,
      cell: row => <AreaDropdown
        selected={row.area}
        areas={areas}
        showUnknown={true}
        onChange={(e) => Patch("temperatures", row._id, "area", e.target.value,
          () => setRefreshKey(k => k + 1),
          () => {}
        )}
      />,
    },
    {name: t("who"),         selector: row => row.created_by, sortable:true},
    {name: t("from"),        selector: row => row.phone,      sortable:true},
  ];

  return (
    <div className="ListHerd">
      {isMobile ? (
        <div className="mobile-search-filter">
          <button
            className={`mobile-filter-toggle ${filtersOpen ? 'open' : ''} ${activeFilterCount > 0 ? 'active' : ''}`}
            onClick={() => setFiltersOpen(o => !o)}
            style={{ width: '100%' }}
          >
            {t("filters")}
            {activeFilterCount > 0 && <span className="mobile-filter-badge">{activeFilterCount}</span>}
          </button>
        </div>
      ) : (
        <div className="desktop-filter-bar">
          <div className="desktop-filter-top">
            <button
              className={`desktop-filter-toggle ${desktopFiltersOpen ? 'open' : ''} ${activeFilterCount > 0 ? 'active' : ''}`}
              onClick={() => setDesktopFiltersOpen(o => !o)}
            >
              {t("filters")} {desktopFiltersOpen ? '▲' : '▼'}
              {activeFilterCount > 0 && <span className="desktop-filter-badge">{activeFilterCount}</span>}
            </button>
            {activeFilterCount > 0 && (
              <button className="desktop-filter-reset" onClick={resetFilters}>
                {t("reset_filters")}
              </button>
            )}
            {actions && <div className="desktop-filter-actions">{actions}</div>}
          </div>
          {desktopFiltersOpen && (
            <div className="desktop-filter-panel">
              <AreaDropdown areas={areas} showAll={true} selected={searchFields.area || ""} onChange={onSearchArea} />
              <select value={dateRange} onChange={onSearchDate}>
                <option value="this_month">{t("this_month")}</option>
                <option value="last_3_months">{t("last_3_months")}</option>
                <option value="last_year">{t("last_year")}</option>
                <option value="">{t("all_time")}</option>
              </select>
            </div>
          )}
        </div>
      )}

      <DataCollection
        key={refreshKey}
        collection={collection}
        columns={columns}
        searchFields={searchFields}
        mobileComponent={(data, onRefresh) => (
          <TemperatureCardList data={data} onRefresh={onRefresh} />
        )}
      />

      {filtersOpen && (
        <>
          <div className="filter-sheet-backdrop" onClick={() => setFiltersOpen(false)} />
          <div className="filter-sheet">
            <div className="filter-sheet-handle" />
            <div className="filter-sheet-header">
              <span className="filter-sheet-title">{t("filters")}</span>
              <button className="filter-sheet-close" onClick={() => setFiltersOpen(false)}>✕</button>
            </div>
            <div className="filter-sheet-body">
              <AreaDropdown areas={areas} showAll={true} selected={searchFields.area || ""} onChange={onSearchArea} />
              <select value={dateRange} onChange={onSearchDate}>
                <option value="this_month">{t("this_month")}</option>
                <option value="last_3_months">{t("last_3_months")}</option>
                <option value="last_year">{t("last_year")}</option>
                <option value="">{t("all_time")}</option>
              </select>
            </div>
            <div className="filter-sheet-footer">
              <button
                className="filter-sheet-reset"
                onClick={resetFilters}
                disabled={activeFilterCount === 0}
              >
                {t("reset_filters")} {activeFilterCount > 0 && `(${activeFilterCount})`}
              </button>
              <button className="filter-sheet-apply" onClick={() => setFiltersOpen(false)}>
                {t("done")}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
export default ListTemperatures;
